import { createContext, useContext, useState, useRef, useEffect } from "react";

const AudioPlayerContext = createContext();

export function AudioPlayerProvider({ children }) {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [playlist, setPlaylist] = useState([]);
  const audioRef = useRef(new Audio());
  // Refs mirror latest state so the once-subscribed audio "ended" handler
  // never reads stale values (fixes auto-advance to the next track).
  const currentTrackRef = useRef(null);
  const playlistRef = useRef([]);
  const playTrackRef = useRef(() => {});

  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  useEffect(() => {
    playlistRef.current = playlist;
  }, [playlist]);

  useEffect(() => {
    const audio = audioRef.current;
    audio.volume = volume;

    const handleTimeUpdate = () => {
      const total = audio.duration && isFinite(audio.duration) ? audio.duration : 0;
      setProgress(total ? (audio.currentTime / total) * 100 : 0);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration && isFinite(audio.duration) ? audio.duration : 0);
    };

    const handleEnded = () => {
      const track = currentTrackRef.current;
      const list = playlistRef.current;
      if (!track || list.length === 0) {
        setIsPlaying(false);
        return;
      }
      const currentIndex = list.findIndex((t) => t.id === track.id);
      const nextIndex = (currentIndex + 1) % list.length;
      playTrackRef.current(list[nextIndex], list);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [volume]);

  const playTrack = (track, tracks = []) => {
    const audio = audioRef.current;
    
    if (currentTrack?.id === track.id && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    if (currentTrack?.id === track.id && !isPlaying) {
      audio.play();
      setIsPlaying(true);
      return;
    }

    const url = track.audio_url?.startsWith("/api/")
      ? `${process.env.REACT_APP_BACKEND_URL}${track.audio_url}`
      : track.audio_url;
    audio.src = url;
    audio.play();
    setCurrentTrack(track);
    setIsPlaying(true);
    if (tracks.length > 0) {
      setPlaylist(tracks);
    }
  };
  playTrackRef.current = playTrack;

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const playNext = () => {
    if (!currentTrack || playlist.length === 0) return;
    const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
    const nextIndex = (currentIndex + 1) % playlist.length;
    playTrack(playlist[nextIndex], playlist);
  };

  const playPrevious = () => {
    if (!currentTrack || playlist.length === 0) return;
    const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
    const prevIndex = (currentIndex - 1 + playlist.length) % playlist.length;
    playTrack(playlist[prevIndex], playlist);
  };

  const seekTo = (percent) => {
    const audio = audioRef.current;
    const total = audio.duration && isFinite(audio.duration) ? audio.duration : 0;
    if (total) audio.currentTime = (percent / 100) * total;
  };

  const changeVolume = (newVolume) => {
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        progress,
        duration,
        volume,
        playTrack,
        togglePlay,
        playNext,
        playPrevious,
        seekTo,
        changeVolume,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error("useAudioPlayer must be used within an AudioPlayerProvider");
  }
  return context;
}
